import { useTranslation } from 'react-i18next';
import { SUBSCRIPTION_STATUSES } from '@d3-moveis/shared';
import { Alert, Box, Button, Grid, MenuItem, TextField } from '@mui/material';
import { DetailSection } from './DetailSection';
import { DetailField } from './DetailField';

const primaryButtonSx = { alignSelf: { xs: 'stretch', sm: 'flex-start' } };

function formatDate(value) {
  if (!value) return '—';
  return new Date(value).toLocaleString();
}

export function OrganizationSubscriptionPanel({
  subscription,
  subscriptionForm,
  onFormChange,
  activePlans,
  onCreate,
  onUpdate,
  isCreating,
  isUpdating,
}) {
  const { t } = useTranslation();

  return (
    <DetailSection title={t('subscriptions.title')}>
      {subscription ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2, sm: 2.5 } }}>
          <Grid container spacing={{ xs: 1.5, sm: 2 }}>
            <DetailField label={t('subscriptions.uuid')} value={subscription.uuid} monospace />
            <DetailField label={t('subscriptions.trialEnds')} value={formatDate(subscription.trialEndsAt)} />
            <DetailField label={t('subscriptions.graceEnds')} value={formatDate(subscription.graceEndsAt)} />
          </Grid>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 2,
            }}
          >
            <TextField
              select
              label={t('subscriptions.plan')}
              value={subscriptionForm.planUuid}
              onChange={(e) => onFormChange({ ...subscriptionForm, planUuid: e.target.value })}
              fullWidth
              size="small"
            >
              {activePlans.map((plan) => (
                <MenuItem key={plan.uuid} value={plan.uuid}>
                  {plan.name} ({plan.slug})
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label={t('subscriptions.status')}
              value={subscriptionForm.status}
              onChange={(e) => onFormChange({ ...subscriptionForm, status: e.target.value })}
              fullWidth
              size="small"
            >
              {SUBSCRIPTION_STATUSES.map((status) => (
                <MenuItem key={status} value={status}>
                  {t(`subscriptions.status.${status}`)}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          <Button variant="contained" onClick={onUpdate} disabled={isUpdating} sx={primaryButtonSx}>
            {t('subscriptions.save')}
          </Button>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Alert severity="info">{t('subscriptions.none')}</Alert>
          <TextField
            select
            label={t('subscriptions.plan')}
            value={subscriptionForm.planUuid}
            onChange={(e) => onFormChange({ ...subscriptionForm, planUuid: e.target.value })}
            fullWidth
            size="small"
            required
          >
            {activePlans.map((plan) => (
              <MenuItem key={plan.uuid} value={plan.uuid}>
                {plan.name} ({plan.slug})
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label={t('subscriptions.status')}
            value={subscriptionForm.status}
            onChange={(e) => onFormChange({ ...subscriptionForm, status: e.target.value })}
            fullWidth
            size="small"
          >
            {SUBSCRIPTION_STATUSES.map((status) => (
              <MenuItem key={status} value={status}>
                {t(`subscriptions.status.${status}`)}
              </MenuItem>
            ))}
          </TextField>
          {subscriptionForm.status === 'trialing' && (
            <TextField
              label={t('subscriptions.trialDays')}
              type="number"
              value={subscriptionForm.trialDays}
              onChange={(e) => onFormChange({ ...subscriptionForm, trialDays: e.target.value })}
              fullWidth
              size="small"
              inputProps={{ min: 0 }}
            />
          )}
          <Button
            variant="contained"
            onClick={onCreate}
            disabled={!subscriptionForm.planUuid || isCreating}
            sx={primaryButtonSx}
          >
            {t('subscriptions.create')}
          </Button>
        </Box>
      )}
    </DetailSection>
  );
}
